import { useQuery, useReactiveVar } from "@apollo/client";
import { currentConfigVar } from "apollo/cache/config";
import { currentDistrictVar } from "apollo/cache/auth";
import { LocationGenerics } from "router/location";
import config from "config";
import {
  dispatchModal,
  closeModal,
  switchModal,
  useTableData,
  useUrlState,
  wrapClick,
  displayUserName,
} from "utils";
import { Avatar, SearchSelectInput, StatusBadge } from "components/core";
import { ActionButton } from "components/buttons";
import { OfficeCalendarHeader, SummaryTabs } from "components/layouts";
import { TableComponent } from "components/tables";
import { Shimmers } from "components/tables";
import lodash from "lodash";
import moment from "moment";
import { FC, useEffect, useMemo } from "react";
import { useNavigate, useSearch } from "react-location";
import { GET_SERVICE_ORDER_SUMMARY, GET_SERVICE_ORDERS } from "./apollo";
import ApproveRATServiceOrderContainer from "./approve";
import AssignRATServiceOrderContainer from "./assign";
import CancelRATServiceOrderContainer from "./cancel";
import DisapproveRATServiceOrderContainer from "./disapprove";
import ViewRATServiceOrderContainer from "./view";

const orderStatuses = [
  { name: "Pending", href: "Pending" },
  { name: "Assigned", href: "Assigned" },
  { name: "In Progress", href: "InProgress" },
  { name: "Resolved", href: "Resolved" },
  { name: "Completed", href: "Completed" },
];

const altOrderStatuses = [
  { name: "Disapproved", href: "Disapproved" },
  { name: "Cancelled", href: "Cancelled" },
];

const validOrderStatuses = lodash.map(
  [...orderStatuses, ...altOrderStatuses],
  "href",
);

const getValidOrderStatus = (orderStatus?: string) => {
  if (!orderStatus) return undefined;
  const isValidStatus = validOrderStatuses.includes(orderStatus);
  return isValidStatus ? orderStatus : undefined;
};

const searchOptions = [
  { label: "Code", value: "code", min: 4 },
  { label: "Contact Name", value: "contactPerson.fullName", min: 4 },
  { label: "Contact Phone Number", value: "contactPerson.phoneNumber", min: 4 },
  { label: "Digital Address", value: "digitalAddress", min: 4 },
];

const cancellableStatuses = ["Pending", "Assigned", "InProgress"];
const assignableStatuses = ["Pending", "Assigned", "InProgress"];

export const RATServiceOrdersPage: FC = () => {
  const { dateFormat } = useReactiveVar(currentConfigVar);
  const currentDistrict = useReactiveVar(currentDistrictVar);

  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const [modal] = useUrlState<string | undefined>("modal");
  const [orderStatus] = useUrlState("orderStatus");
  const [priority, setPriority] = useUrlState("priority");

  const filter = useMemo(
    () => ({
      page: Number(searchParams.page || config.constants.page),
      pageSize: Number(searchParams.pageSize || config.constants.pageSize),
      search: searchParams.search || undefined,
      searchFields: lodash.map(searchOptions, "value"),
      sort: searchParams.sort || "-createdAt",
      fromDate: searchParams.fromDate || undefined,
      toDate: searchParams.toDate || undefined,
      region: searchParams.region || undefined,
      ...(currentDistrict ? { district: currentDistrict } : {}),
      status: getValidOrderStatus(searchParams.orderStatus),
      priority: searchParams.priority || undefined,
    }),
    [searchParams, currentDistrict],
  );

  const { data, loading, networkStatus, refetch } = useQuery(
    GET_SERVICE_ORDERS,
    {
      variables: filter,
      notifyOnNetworkStatusChange: false,
      onCompleted: () => {
        refetchSummary();
      },
    },
  );

  const { data: dataSummary, refetch: refetchSummary } = useQuery(
    GET_SERVICE_ORDER_SUMMARY,
    {
      variables: filter,
      notifyOnNetworkStatusChange: false,
    },
  );

  const records = useTableData(data || {});

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        ...(!old?.page ? { page: config.constants.page } : {}),
        ...(!old?.pageSize ? { pageSize: config.constants.pageSize } : {}),
        ...(!old?.search ? {} : { search: "" }),
        ...(!old?.sort ? { sort: "-createdAt" } : {}),
      }),
    });
  }, [navigate]);

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        id: undefined,
      }),
    });
  }, [searchParams.orderStatus, navigate]);

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <OfficeCalendarHeader
        views={["all-time", "day", "month", "custom"]}
        defaultView="month"
      />
      <SummaryTabs
        tabs={orderStatuses}
        altTabs={altOrderStatuses}
        summary={dataSummary?.summary}
        param={"orderStatus"}
        label="Orders"
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 min-h-full mx-auto min-w-0 py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            title={"readiness assessment test orders"}
            refetch={refetch}
            isRefetching={loading && networkStatus === 4}
            loading={loading && ![4, 6].includes(networkStatus)}
            data={records}
            hasSearch={true}
            renderHeaderItems={() => (
              <div className="mt-4 sm:mt-0.5 sm:ml-16 sm:flex space-x-2">
                <div>
                  <SearchSelectInput
                    id="priority"
                    options={[
                      { label: { title: "Low" }, value: "Low" },
                      { label: { title: "Medium" }, value: "Medium" },
                      { label: { title: "High" }, value: "High" },
                      { label: { title: "Critical" }, value: "Critical" },
                    ]}
                    placeholder="Select Priority"
                    setFieldValue={(_: any, value: string) =>
                      setPriority(value)
                    }
                    values={{ priority }}
                    labelHidden
                    label={""}
                  />
                </div>
              </div>
            )}
            renderColumns={() => (
              <tr>
                <th
                  scope="col"
                  className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Code | Date
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Customer
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Digital Address | Location
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Priority
                </th>
                {["Assigned", "InProgress", "Resolved"].includes(
                  orderStatus,
                ) && (
                  <th
                    scope="col"
                    className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-right text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                  >
                    Assigned | ETA
                  </th>
                )}
                {["Disapproved"].includes(orderStatus) && (
                  <th
                    scope="col"
                    className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-right text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                  >
                    Disapproved
                  </th>
                )}
                {["Cancelled"].includes(orderStatus) && (
                  <th
                    scope="col"
                    className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-right text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                  >
                    Cancelled
                  </th>
                )}
                {["Completed"].includes(orderStatus) && (
                  <>
                    <th
                      scope="col"
                      className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-right text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                    >
                      Resolution Period
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                    >
                      Result
                    </th>
                  </>
                )}
                {[
                  "Assigned",
                  "InProgress",
                  "Resolved",
                  "Completed",
                ].includes(orderStatus) && (
                  <>
                    <th
                      scope="col"
                      className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                    >
                      Assigner
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                    >
                      Assignee
                    </th>
                  </>
                )}
                {["Completed", "Disapproved"].includes(orderStatus) && (
                  <th
                    scope="col"
                    className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                  >
                    {orderStatus === "Disapproved" ? "Disapprover" : "Approver"}
                  </th>
                )}
                {!orderStatus && (
                  <th
                    scope="col"
                    className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                  >
                    Status
                  </th>
                )}
                <th
                  scope="col"
                  className="sticky top-0 border-b border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                ></th>
              </tr>
            )}
            renderLoader={() => (
              <tr>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.DoubleShimmer />
                </td>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.DoubleShimmer />
                </td>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.DoubleShimmer />
                </td>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.SingleShimmer />
                </td>
                {!orderStatus && (
                  <td className="px-6 py-4 border-b border-gray-200">
                    <Shimmers.SingleShimmer />
                  </td>
                )}
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.ActionsShimmer actionsCount={4} />
                </td>
              </tr>
            )}
            renderItem={(item) => (
              <tr
                key={item._id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                onClick={wrapClick(dispatchModal(item._id, "view"))}
              >
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {item?.code || "N/A"}
                  </div>
                  <div className="font-medium text-gray-500 dark:text-gray-400">
                    {moment(item?.createdAt).format(dateFormat)}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <div className="text-gray-900 dark:text-gray-100">
                    {item?.contactPerson?.fullName || "N/A"}
                  </div>
                  <div className="text-gray-500 dark:text-gray-500">
                    {item?.contactPerson?.phoneNumber || "N/A"}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <div className="text-gray-900 dark:text-gray-100">
                    {item?.digitalAddress || "N/A"}
                  </div>
                  <div className="text-gray-500 dark:text-gray-500">
                    {[item?.region?.name, item?.district?.name]
                      .filter(Boolean)
                      .join(" · ") || "N/A"}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <StatusBadge
                    status={item?.priority}
                    options={{
                      High: "bg-orange-100 text-orange-800",
                      Low: "bg-gray-100 text-gray-800",
                      Critical: "bg-red-100 text-red-800",
                      Medium: "bg-yellow-100 text-yellow-800",
                    }}
                  />
                </td>
                {["Assigned", "InProgress", "Resolved"].includes(
                  orderStatus,
                ) && (
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 text-right">
                    <div className="text-gray-900 dark:text-gray-100">
                      {item?.assignedAt
                        ? moment(item?.assignedAt).format(dateFormat)
                        : "N/A"}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">
                      {item?.estimatedResolutionDate
                        ? moment(item?.estimatedResolutionDate).format(
                            dateFormat,
                          )
                        : "N/A"}
                    </div>
                  </td>
                )}
                {["Disapproved"].includes(orderStatus) && (
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 text-right">
                    <div className="text-gray-900 dark:text-gray-100">
                      <span className="text-xs mr-2 font-light">from</span>
                      {item?.assignedAt
                        ? moment(item?.assignedAt).format(dateFormat)
                        : "N/A"}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">
                      <span className="text-xs mr-2 font-light">to</span>
                      {item?.disapprovedAt
                        ? moment(item?.disapprovedAt).format(dateFormat)
                        : "N/A"}
                    </div>
                  </td>
                )}
                {["Cancelled"].includes(orderStatus) && (
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 text-right">
                    <div className="text-gray-900 dark:text-gray-100">
                      {item?.cancelledAt
                        ? moment(item?.cancelledAt).format(dateFormat)
                        : "N/A"}
                    </div>
                  </td>
                )}
                {["Completed"].includes(orderStatus) && (
                  <>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 text-right">
                      <div className="text-gray-900 dark:text-gray-100">
                        <span className="text-xs mr-2 font-light">from</span>
                        {item?.assignedAt
                          ? moment(item?.assignedAt).format(dateFormat)
                          : "N/A"}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        <span className="text-xs mr-2 font-light">to</span>
                        {item?.completedAt
                          ? moment(item?.completedAt).format(dateFormat)
                          : "N/A"}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                      <StatusBadge
                        status={item?.result}
                        options={{
                          Failed: "bg-orange-100 text-orange-800",
                          Passed: "bg-green-100 text-green-800",
                        }}
                      />
                    </td>
                  </>
                )}
                {[
                  "Assigned",
                  "InProgress",
                  "Resolved",
                  "Completed",
                ].includes(orderStatus) && (
                  <>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <Avatar
                            disabled={true}
                            alt={displayUserName(item?.assigner) || "N A"}
                            src={item?.assigner?.profileImageUrl || ""}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-gray-900 dark:text-gray-100">
                            {displayUserName(item?.assigner) || "N A"}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400">
                            {item?.assigner?.phoneNumber || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <Avatar
                            disabled={true}
                            alt={displayUserName(item?.assignee) || "N A"}
                            src={item?.assignee?.profileImageUrl || ""}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-gray-900 dark:text-gray-100">
                            {displayUserName(item?.assignee) || "N A"}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400">
                            {item?.assignee?.phoneNumber || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                  </>
                )}
                {["Completed", "Disapproved"].includes(orderStatus) && (
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <Avatar
                          disabled={true}
                          alt={
                            displayUserName(
                              orderStatus === "Disapproved"
                                ? item?.disapprover
                                : item?.approver,
                            ) || "N A"
                          }
                          src={
                            (orderStatus === "Disapproved"
                              ? item?.disapprover?.profileImageUrl
                              : item?.approver?.profileImageUrl) || ""
                          }
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-gray-900 dark:text-gray-100">
                          {displayUserName(
                            orderStatus === "Disapproved"
                              ? item?.disapprover
                              : item?.approver,
                          ) || "N A"}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400">
                          {(orderStatus === "Disapproved"
                            ? item?.disapprover?.phoneNumber
                            : item?.approver?.phoneNumber) || "N/A"}
                        </div>
                      </div>
                    </div>
                  </td>
                )}
                {!orderStatus && (
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                    <StatusBadge
                      status={item?.status}
                      options={{
                        Pending: "bg-yellow-100 text-yellow-800",
                        Assigned: "bg-blue-100 text-blue-800",
                        InProgress: "bg-indigo-100 text-indigo-800",
                        Resolved: "bg-purple-100 text-purple-800",
                        Completed: "bg-green-100 text-green-800",
                        Disapproved: "bg-red-100 text-red-800",
                        Cancelled: "bg-gray-100 text-gray-800",
                      }}
                    />
                  </td>
                )}
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 text-right">
                  <div className="space-x-1">
                    <ActionButton
                      action="view"
                      onClick={dispatchModal(item?._id, "view")}
                    />
                    {assignableStatuses.includes(item?.status) &&
                      assignableStatuses.includes(orderStatus || item?.status) && (
                        <ActionButton
                          action={
                            item?.status === "Pending" ? "assign" : "reassign"
                          }
                          onClick={dispatchModal(item?._id, "assign")}
                        />
                      )}
                    {item?.status === "Resolved" &&
                      orderStatus === "Resolved" && (
                        <>
                          <ActionButton
                            action="approve"
                            onClick={dispatchModal(item?._id, "approve")}
                          />
                          <ActionButton
                            action="disapprove"
                            onClick={dispatchModal(item?._id, "disapprove")}
                          />
                        </>
                      )}
                    {cancellableStatuses.includes(item?.status) &&
                      (orderStatus
                        ? cancellableStatuses.includes(orderStatus)
                        : true) && (
                        <ActionButton
                          action="cancel"
                          onClick={dispatchModal(item?._id, "cancel")}
                        />
                      )}
                  </div>
                </td>
              </tr>
            )}
          />
        </div>
      </div>

      {Boolean(searchParams.id?.length) && (
        <>
          <AssignRATServiceOrderContainer
            open={modal === "assign"}
            setOpen={switchModal("view")}
            refetch={refetch}
          />
          <ApproveRATServiceOrderContainer
            open={modal === "approve"}
            setOpen={switchModal("view")}
            refetch={refetch}
          />
          <DisapproveRATServiceOrderContainer
            open={modal === "disapprove"}
            setOpen={switchModal("view")}
            refetch={refetch}
          />
          <CancelRATServiceOrderContainer
            open={modal === "cancel"}
            setOpen={switchModal("view")}
            refetch={refetch}
          />
          <ViewRATServiceOrderContainer
            open={modal === "view"}
            setOpen={closeModal}
          />
        </>
      )}
    </main>
  );
};

export default RATServiceOrdersPage;
